import axiosNest from '../api/axiosNest';
import { toErrorMessage } from '../admin/userOffers/orderActionsApi';

/**
 * Zapis zlecenia do VIA (W10).
 *
 * Odpowiada `apps/api/src/via/zapis-via.controller.ts`. Trzy osobne
 * wywołania, bo akceptacja ma być czynnością operatora, a nie skutkiem
 * ubocznym podglądu. Nie ma tu funkcji „zrób wszystko naraz" i nie ma jej
 * świadomie — patrz komentarz przy kontrolerze.
 */

export interface ViaWritePreview {
  orderId: number;
  state: string;
  xml: string;
  errors: string[];
  ostrzezenia: string[];
  canWrite: boolean;
  blockReason: string | null;
  /** Dokąd pójdzie plik — tylko odczyt, ustawia wdrożenie (env). */
  delivery?: ViaDeliveryInfo;
  /** Ostatni zapis: wynik dostarczenia, komunikat przy błędzie. */
  lastWrite?: ViaLastWrite | null;
  /**
   * Pliki jednej akcji zapisu (16.10.2026): zlecenie i — przy wymaganym
   * typie z „+FORKLIFT” — czynność wózkowa (odpowiedzi Marathonu/Euvic,
   * pyt. 39e). `xml` i `errors` wyżej to plik główny i błędy obu plików.
   */
  files?: ViaWriteFile[];
  /** Ostatni zapis każdego pliku (`recordKind`). */
  lastWrites?: ViaLastWrite[];
  /**
   * Podobne zlecenia w banku (16.10.2026, przełącznik
   * `viaWriteDuplicateCheck`; `null` = wyłączony albo zlecenie już w VIA).
   */
  duplicates?: ViaDuplicateCheck | null;
  /**
   * Eksport do TMS jedną akcją (21.10.2026, przełącznik `tmsExportOneStep`):
   * czy „Potwierdź zapis” zatwierdzi i wytworzy pliki w tym stanie.
   */
  canExport?: boolean;
  exportBlockReason?: string | null;
  /**
   * Dlaczego wysyłka do TMS teraz nie zadziała — konfiguracja albo brak
   * poświadczeń (25.10.2026); pokazywane jako błąd w oknie eksportu.
   */
  deliveryBlockReason?: string | null;
  /**
   * Automatyczne ponawianie dostarczenia (21.10.2026, przełącznik
   * `viaDeliveryAutoRetry`; `null` = wyłączony).
   */
  deliveryRetry?: ViaDeliveryRetry | null;
  /**
   * Ślad prób dostarczenia (poprawka obowiązkowa 21.10.2026): każda próba
   * osobno, najnowsze pierwsze. Bez przełącznika.
   */
  attempts?: ViaAttempt[];
}

/** Jedna próba dostarczenia pliku — `trigger` to dane z API. */
export interface ViaAttempt {
  at: string;
  kind: string;
  trigger: string;
  triggerLabel: string;
  ok: boolean;
  destination: string | null;
  errorMessage: string | null;
  byName: string | null;
}

/** Stan ponawiania jednego pliku — `state` to dane z API. */
export interface ViaAutoRetryView {
  state: 'pending' | 'exhausted' | null;
  done: number;
  max: number;
  nextAt: string | null;
  exhaustedAt: string | null;
}

export interface ViaDeliveryRetry {
  params: { attempts: number; intervalMin: number };
  files: Array<{ kind: string; retry: ViaAutoRetryView }>;
}

/** Jeden plik zapisu w podglądzie — `kind` to dane (`MAIN` / `FORKLIFT`). */
export interface ViaWriteFile {
  kind: string;
  /** Nazwa dla operatora: „zlecenie”, „czynność wózkowa”. */
  label: string;
  fileName: string;
  xml: string;
  errors: string[];
  /** Suma treści — „Potwierdź zapis” ją odsyła (zatwierdza się to, co widać). */
  checksum?: string;
}

/** Przełącznik ostrzeżenia o duplikacie przed zapisem (16.10.2026). */
export const FEATURE_VIA_WRITE_DUPLICATE_CHECK = 'viaWriteDuplicateCheck';

/** Najkrótszy powód „to nie duplikat” — lustro API (`duplicate-check.ts`). */
export const MIN_NOT_DUPLICATE_REASON_LENGTH = 10;

/** Potwierdzenie „to nie duplikat” — kto, kiedy, dlaczego. */
export interface ViaNotDuplicateConfirmation {
  at: string;
  by: string | null;
  reason: string | null;
}

/** Zlecenie z banku, które może być tym samym transportem. */
export interface ViaDuplicateCandidate {
  userOfferId: number;
  kind: 'tms' | 'preOrder';
  /** Numer zlecenia w TMS albo nasz numer (prezlecenie po akceptacji). */
  number: string | null;
  orderId: number | null;
  viaWriteStatus: string | null;
  clientName: string | null;
  tmsReferenceNumber: string | null;
  /** Numer zamówienia klienta kandydata z prezlecenia (16.10.2026). */
  customerOrderNumber?: string | null;
  fromDate: string;
  toDate: string;
  fromCity: string | null;
  toCity: string | null;
  fromCountry: string | null;
  toCountry: string | null;
  /** Co się zgadza — zdania z API. */
  reasons: string[];
  confirmation: ViaNotDuplicateConfirmation | null;
}

export interface ViaDuplicateCheck {
  checked: boolean;
  uncheckedReason: string | null;
  candidates: ViaDuplicateCandidate[];
  unconfirmed: number;
  /** Zdanie odmowy zapisu, dopóki są niepotwierdzeni; `null` = wolno. */
  writeRefusal: string | null;
  /** Ręczne łączenie z TMS działa (`preOrderTmsManualLink`). */
  manualLinkAvailable: boolean;
}

/**
 * Tryb dostarczania pliku (15.10.2026): katalog na serwerze aplikacji albo
 * serwer SFTP importu TMS albo folder sieciowy TMS (28.10.2026), katalog
 * `preprod` / `prod`. Bez adresu serwera.
 */
export interface ViaDeliveryInfo {
  mode: 'directory' | 'sftp' | 'folder' | null;
  target: 'preprod' | 'prod' | null;
  label: string;
  problem: string | null;
  /**
   * Czy plik w tym trybie może dotrzeć do importu TMS (25.10.2026). Katalog
   * na serwerze aplikacji — nie: zapis kończy się błędem (decyzja
   * użytkownika 24.10.2026). Brak pola (starsze API) = nie wiemy.
   */
  reachesTms?: boolean;
}

export interface ViaLastWrite {
  id: number;
  /** Rodzaj pliku (`MAIN` / `FORKLIFT`) — dane w bazie. */
  recordKind?: string;
  status: string;
  attempt: number;
  xmlFileName: string | null;
  destination: string | null;
  deliveredAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * `ViaWrite.destination` słowem — kod w bazie (`katalog`, `sftp:preprod`,
 * `sftp:prod`, `folder:preprod`, `folder:prod`) zostaje kodem. Brak = zapis
 * sprzed 15.10.2026.
 */
export function viaDestinationLabel(destination?: string | null): string {
  switch (destination) {
    case 'katalog':
      return 'katalog na serwerze aplikacji';
    case 'sftp:preprod':
      return 'SFTP preprod';
    case 'sftp:prod':
      return 'SFTP prod';
    case 'folder:preprod':
      return 'folder sieciowy preprod';
    case 'folder:prod':
      return 'folder sieciowy prod';
    case null:
    case undefined:
    case '':
      return '—';
    default:
      return destination;
  }
}

const path = (id: number | string) => `/via-write/${id}`;

/** Podgląd pliku i lista braków. Nie zmienia niczego po stronie serwera. */
export async function fetchViaWritePreview(
  id: number | string,
): Promise<ViaWritePreview> {
  const { data } = await axiosNest.get<ViaWritePreview>(path(id));
  return data;
}

/** Jawna akceptacja operatora. */
export async function approveViaWrite(id: number | string) {
  const { data } = await axiosNest.post(`${path(id)}/approve`);
  return data;
}

/**
 * Eksport do TMS jedną akcją (paczka p. Mariusza 21.10, BR-ADM-18):
 * zatwierdzenie i wytworzenie plików — tylko treści z podglądu, której sumy
 * idą w żądaniu. Serwer odmawia (409), gdy dane zmieniły się od podglądu.
 */
export async function exportToTms(id: number | string, checksums: string[]) {
  const { data } = await axiosNest.post(`${path(id)}/export`, { checksums });
  return data as { viaWriteStatus?: string; approvedBy?: string | null; files?: Array<{ kind: string }> };
}

/** Wytworzenie pliku w skrzynce nadawczej. */
export async function generateViaFile(id: number | string) {
  const { data } = await axiosNest.post(`${path(id)}/write`);
  return data;
}

export interface RetryResult {
  id: number;
  xmlFileName: string | null;
  attempt: number;
  sameContent: boolean;
  fileWasInOutbox: boolean;
  note: string | null;
}

/**
 * Ponowienie: ten sam plik jeszcze raz w skrzynce.
 *
 * Osobne wywołanie od `generateViaFile`, bo to inna decyzja — „wiem, że plik
 * wyszedł, i świadomie kładę go ponownie”. O tym, czy wolno, rozstrzyga
 * serwer; przeglądarka nie ma kopii tej reguły.
 */
/**
 * Oświadczenie: „usunąłem zlecenie w VIA” — jedyna droga powrotu dla
 * zlecenia przyjętego u klienta. Powód jest obowiązkowy, bo to słowo
 * człowieka, nie potwierdzenie z VIA; ocenia je serwer.
 */
export async function withdrawFromVia(id: number | string, reason: string) {
  const { data } = await axiosNest.post(`${path(id)}/withdraw-from-via`, {
    reason,
  });
  return data as { id: number; viaWriteStatus: string; description: string };
}

export async function retryViaWrite(
  id: number | string,
): Promise<RetryResult> {
  const { data } = await axiosNest.post<RetryResult>(
    `${path(id)}/retry`,
  );
  return data;
}

/**
 * „To nie duplikat” (16.10.2026): operator sprawdził podobne zlecenia
 * (`userOfferIds` — te, które widział) i potwierdza z powodem. Wpis
 * w historii obu zleceń; serwer odmawia, gdy lista się zmieniła.
 */
export async function confirmNotDuplicate(
  id: number | string,
  userOfferIds: number[],
  reason: string,
) {
  const { data } = await axiosNest.post(`${path(id)}/not-duplicate`, {
    userOfferIds,
    reason,
  });
  return data as { confirmed: number[]; remaining: number };
}

export { toErrorMessage };
